import { useEffect } from 'react';

export default function PrivacyPolicy() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Privacy Policy | Maxed';
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <main className="privacy-page">
      <article className="privacy-card">
        <header className="privacy-header">
          <a className="privacy-brand" href="/">
            <img className="brand-mark" src="/brand/maxed-logo.webp" alt="" />
            <span>Maxed</span>
          </a>
          <a className="privacy-back" href="/">Back to sign up</a>
        </header>

        <p className="eyebrow">Effective August 5, 2026</p>
        <h1>Privacy Policy</h1>
        <p className="privacy-lead">Maxed provides private, AI-assisted multilingual meeting practice. This policy explains what information we collect, why we use it, where it goes, and the choices you have.</p>

        <section>
          <h2>Information we collect</h2>
          <p><strong>Account information.</strong> When you create an account, we receive your email address and account identifier. If you use a third-party sign-in provider, we may also receive your name and profile photo.</p>
          <p><strong>Meeting practice content.</strong> We process the language, role, scenario, difficulty, responses, transcripts, and vocabulary report cards associated with your practice sessions.</p>
          <p><strong>Files and references.</strong> If you choose to personalize a session, we process files such as agendas, notes, slides, and vocabulary lists, selected content from a public reference, or supported files you import from a Google Drive folder.</p>
          <p><strong>Google Drive access.</strong> Drive access is optional and may use a Google account different from the account you use to sign in to Maxed. Maxed requests read-only access after you choose this feature. The short-lived Google access token stays in browser memory and is not stored by Maxed; imported file copies are stored as meeting context under your Maxed account.</p>
          <p><strong>Project sharing.</strong> If a project owner shares a project, invited viewers and editors can access its attached files and imported Google Drive material. Editors can add or remove shared project assets. Setup conversations, settings prompts, practice sessions, transcripts, and report cards remain private to the individual user and are not shared with other project members.</p>
          <p><strong>Voice and avatar sessions.</strong> We process microphone audio to transcribe and conduct the practice meeting. Voice and avatar sessions may be recorded as mixed audio and stored with the session.</p>
          <p><strong>Technical information.</strong> Our infrastructure and service providers may log IP addresses, browser or device details, request metadata, errors, and security events needed to operate and protect the service.</p>
        </section>

        <section>
          <h2>How we use information</h2>
          <p>We use this information to authenticate you, run and personalize meeting practice, generate transcripts and vocabulary report cards, preserve your practice history, process account deletion, prevent abuse, troubleshoot failures, and improve the reliability of Maxed.</p>
          <p>We do not sell your personal information or use your practice content for targeted advertising.</p>
        </section>

        <section>
          <h2>Service providers</h2>
          <p>We use service providers for account authentication; cloud hosting, databases, and private storage; AI model processing; speech recognition and synthesis; public-reference retrieval; and network connectivity. Depending on the features you use, these providers may process account identifiers, meeting prompts and responses, selected context, audio, transcripts, or technical logs as needed to operate Maxed.</p>
          <p>These providers process data under their own terms and may operate in countries outside yours.</p>
        </section>

        <section>
          <h2>Storage and retention</h2>
          <p>Account records, meeting practice sessions, transcripts, uploaded context, and recordings are retained while your account is active so you can use your history and context again. Generated speech cache files are scheduled for deletion after 30 days.</p>
          <p>When you request account deletion, Maxed queues deletion of your live authentication identity, database records, uploaded files, transcripts, and recordings. Encrypted database backups, recovery logs, and soft-deleted storage copies may remain for limited recovery windows before expiring. Operational event records may retain identifiers needed to document processing and diagnose failures.</p>
        </section>

        <section>
          <h2>Your choices</h2>
          <p>You decide whether to upload files, add a public reference, connect Google Drive, or use microphone-based practice. You can revoke Maxed's Drive access from your Google Account permissions and request deletion from the account controls in the app. To request access, correction, or help with deletion, contact us at <a href="mailto:hello@maxed.jp">hello@maxed.jp</a>.</p>
        </section>

        <section>
          <h2>Security</h2>
          <p>We use authenticated access, encrypted connections, private cloud storage, service identities, and access controls designed to protect your information. No online service can guarantee absolute security.</p>
        </section>

        <section>
          <h2>Children</h2>
          <p>Maxed is not directed to children under 16, and we do not knowingly collect their personal information. Contact us if you believe a child has provided information to the service.</p>
        </section>

        <section>
          <h2>Changes and contact</h2>
          <p>We may update this policy as the service changes. We will revise the effective date and provide additional notice when a change materially affects how we use personal information.</p>
          <p>Maxed is the operator responsible for this policy. Questions can be sent to <a href="mailto:hello@maxed.jp">hello@maxed.jp</a>.</p>
        </section>
      </article>
    </main>
  );
}
